import { Injectable, Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'prettyNumber',
  standalone: true,
})
@Injectable({ providedIn: 'root' })
export class PrettyNumberPipe implements PipeTransform {
  private static readonly DEFAULT_FRAC_DIGITS = 2;
  private static readonly PERCENT_FRAC_DIGITS = 4;

  transform(
    value: number | null | undefined,
    abbreviate = true,
    unit?: string
  ): string {
    if (value == null || !Number.isFinite(value)) return '';

    // Normalize -0 to 0
    const v = Object.is(value, -0) ? 0 : value;

    // Special rule: if percentage, force 4 decimals (ignore abbreviate flag)
    if (unit === '%') {
      const rounded = this.roundTo(v, PrettyNumberPipe.PERCENT_FRAC_DIGITS);
      const formatted = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: PrettyNumberPipe.PERCENT_FRAC_DIGITS,
      }).format(rounded);
      return this.withUnit(formatted, unit);
    }

    // Otherwise, standard handling
    const formatted = abbreviate
      ? this.formatAbbreviated(v)
      : this.formatStandard(v);

    return unit ? this.withUnit(formatted, unit) : formatted;
  }

  private formatAbbreviated(value: number): string {
    const abs = Math.abs(value);

    if (abs < 1_000) {
      return this.formatStandard(value);
    }

    return new Intl.NumberFormat(undefined, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  }

  private formatStandard(value: number): string {
    const rounded = this.roundTo(value, PrettyNumberPipe.DEFAULT_FRAC_DIGITS);
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: PrettyNumberPipe.DEFAULT_FRAC_DIGITS,
    }).format(rounded);
  }

  private roundTo(value: number, digits: number): number {
    const pow = Math.pow(10, digits);
    return Math.round(value * pow) / pow;
  }

  private withUnit(val: string, unit: string): string {
    const sep = unit === '%' ? '' : '\u00A0'; // non-breaking space for non-% units
    return `${val}${sep}${unit}`;
  }
}
